import React, { FC, useMemo } from 'react';

import { Control } from 'react-hook-form';

import { FontAwesomeIcon } from '@appcharge/shared-ui';

import Input from 'components/Input/Input';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { MultiSelect, MultiSelectOption } from 'components/MultiSelect/MultiSelect';
import RichText from 'components/RichText/RichText';
import { FormControl, FormField, FormItem, UILabel } from 'components/ui';

import {
  EPopupTriggersAction,
  PopupsFormFields
} from '../../PopupsForm.consts';
import { Chip } from '@appcharge/shared-ui';

import { createNumberInputHandlers } from '../../PopupsForm.utils';

const CustomChip = (): React.ReactElement => (
  <div className="w-full mt-2.5 justify-center flex items-center">
    <div className="h-1.5 w-1.5 bg-indigo-600 rounded-full" />
    <div className="w-6 border-[1px] border-indigo-600 h-[1px] border-dashed mx-0.5" />
    <Chip className=" bg-violet-100 text-blue-950">And</Chip>
    <div className="w-6 border-[1px] border-indigo-600 h-[1px] border-dashed mx-0.5" />
    <div className="h-1.5 w-1.5 bg-indigo-600 rounded-full" />
  </div>
);

const TRIGGER_ACTION_OPTIONS = [
  {
    value: EPopupTriggersAction.PURCHASE_COMPLETED,
    label: 'Purchase completed'
  },
  {
    value: EPopupTriggersAction.STORE_REFRESH,
    label: 'Store refresh'
  }
];

interface IOfferIdItem {
  value: string;
  key: string;
  content: string;
}

interface ITriggersSectionProps {
  control: Control<PopupsFormFields>;
  offersIds: IOfferIdItem[];
  isTriggeredPopups?: boolean;
  triggersAction?: EPopupTriggersAction;
  onTriggersActionChange?: (action: EPopupTriggersAction) => void;
}

const TriggersSection: FC<ITriggersSectionProps> = ({
  control,
  offersIds,
  isTriggeredPopups = true,
  triggersAction,
  onTriggersActionChange
}) => {
  const isStoreRefresh = triggersAction === EPopupTriggersAction.STORE_REFRESH;

  const offerIdOptions = useMemo<MultiSelectOption[]>(
    () =>
      offersIds.map((offer) => ({
        value: offer.value,
        label: offer.content
      })),
    [offersIds]
  );

  const renderIdValue = (selected: string[]): string => {
    const selectedLength = selected?.length ?? 0;
    if (selectedLength === 0) return 'Select';
    if (selectedLength < offersIds.length && selectedLength > 1)
      return `${selected[0]} and ${selectedLength - 1} more...`;
    if (selectedLength === 1) return selected[0];
    return 'All';
  };

  let actionOptions = [TRIGGER_ACTION_OPTIONS[0]];
  if (isTriggeredPopups) {
    actionOptions = TRIGGER_ACTION_OPTIONS;
  }

  return (
    <div>
      <RichText
        title="Triggers"
        description="Define when the offer will be presented to the player"
        className="mb-5"
      />
      <FormField
        control={control}
        name="triggersAction"
        render={({ field: { value, onChange } }) => (
          <FormItem>
            <FormControl>
              <InputSelectDropdown
                isRequired
                key={value}
                label="Action"
                defaultSelected={actionOptions.find(
                  (opt) => opt.value === value
                )}
                options={actionOptions}
                onOptionChange={(newValue: string): void => {
                  onChange(newValue);
                  onTriggersActionChange?.(newValue as EPopupTriggersAction);
                }}
              />
            </FormControl>
          </FormItem>
        )}
      />
      <CustomChip />
      <FormField
        control={control}
        name="triggersFrequency"
        render={({ field: { value, onChange } }) => (
          <FormItem>
            <FormControl>
              <Input
                min={1}
                label="Frequency"
                type="number"
                data-testid="triggers-frequency-input"
                value={value}
                tooltipIcon="fa-circle-exclamation"
                infoTooltipContent="Frequency controls how often the popup appears when its conditions are met"
                {...createNumberInputHandlers(onChange, 1)}
              />
            </FormControl>
          </FormItem>
        )}
      />
      <CustomChip />
      {isStoreRefresh && (
        <FormField
          control={control}
          name="coolDownInMinutes"
          render={({ field: { value, onChange } }) => (
            <FormItem>
              <FormControl>
                <Input
                  label="Cooldown"
                  type="number"
                  min={1}
                  value={value ?? ''}
                  placeholder="1 Min"
                  tooltipIcon="fa-circle-exclamation"
                  infoTooltipContent="Cooldown (in minutes) specifies the minimum time between consecutive displays of the offer."
                  {...createNumberInputHandlers(onChange, 1)}
                />
              </FormControl>
            </FormItem>
          )}
        />
      )}
      {!isStoreRefresh && (
        <FormField
          control={control}
          name="triggersOfferExternalIds"
          render={({ field: { value, onChange } }) => (
            <FormItem>
              <FormControl>
                <div className="flex flex-col w-full">
                  <UILabel
                    htmlFor="triggers-offer-ids"
                    className="flex text-p-s font-medium text-base-foreground mb-2"
                  >
                    <div className="mr-2 mt-1">
                      <FontAwesomeIcon
                        icon="fa-regular fa-asterisk"
                        className="h-2 w-2 text-[8px]"
                      />
                    </div>
                    Offer ID
                  </UILabel>
                  <MultiSelect
                    options={offerIdOptions}
                    value={value || []}
                    onChange={onChange}
                    placeholder="Select"
                    renderValue={renderIdValue}
                    showSelectAll
                    boxDataTestId="popupExternalIdsBox"
                    listDataTestId="dashPopupExternalIdsList"
                  />
                </div>
              </FormControl>
            </FormItem>
          )}
        />
      )}
    </div>
  );
};

export { TriggersSection };
